// LinkTree event tracking — uses fetch+keepalive so an in-flight request
// survives the page navigation that follows a link click (sendBeacon can't
// be used here because Supabase REST requires the apikey/Authorization
// headers and sendBeacon doesn't let us set them).
//
// We pull the URL/key from the shared supabase.ts module so we inherit its
// baked-in fallbacks — env vars aren't always available at build time
// (e.g. preview builds), and without those fallbacks every tracking
// request would be a no-op.

import { supabaseUrl as SUPABASE_URL, supabaseAnonKey as SUPABASE_ANON_KEY } from '@/lib/supabase';

const SESSION_KEY = 'lt_session_id';
const MAX_REFERRER_LEN = 512;
const MAX_URL_LEN = 2048;

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch { return newId(); }
}

function detectDevice(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
  return 'desktop';
}

/** Skip analytics inserts for visits that are obviously not human:
 *  - WebDriver / Puppeteer / Playwright (uses navigator.webdriver flag)
 *  - User-agent matches a common bot/crawler/headless pattern
 *  - The page is being prerendered (browser hint, not yet visible)
 *  This keeps the analytics counts representative — without it, Lighthouse,
 *  uptime monitors, link unfurlers and SEO crawlers inflate every metric.
 */
function isLikelyBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  if ((navigator as any).webdriver === true) return true;
  const ua = navigator.userAgent || '';
  if (/bot|crawler|spider|crawling|headless|lighthouse|prerender|slurp|facebookexternalhit|twitterbot|linkedinbot|slackbot|discordbot|telegrambot|whatsapp|skypeuripreview|embedly|iframely|applebot|bingbot|googlebot|pinterest|baiduspider|yandex/i.test(ua)) return true;
  if (typeof document !== 'undefined' && (document.visibilityState as string) === 'prerender') return true;
  return false;
}

function logDev(label: string, err: unknown) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[linktree] ${label}`, err);
  }
}

/** Truncate to a safe length so a malicious or buggy referrer can't fill
 *  the column. The DB column is TEXT but we still want bounded payloads. */
function clamp(s: string | null | undefined, max: number): string | null {
  if (!s) return null;
  return s.length > max ? s.slice(0, max) : s;
}

/** Lightweight, PII-free user-agent fingerprint. djb2 over the UA string,
 *  hex-encoded. ~32 bits — plenty for "is this the same browser?" bucketing
 *  in analytics dedup without identifying the user. Synchronous so it can
 *  be embedded in fetch+keepalive bodies. */
function uaHash(): string {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (!ua) return '';
  let h = 5381;
  for (let i = 0; i < ua.length; i++) h = ((h << 5) + h) ^ ua.charCodeAt(i);
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Fire an event via fetch+keepalive so the request finishes even after the
 *  current page is unloaded (e.g. on a link click that navigates away). */
function beacon(table: 'linktree_page_views' | 'linktree_link_clicks', body: Record<string, unknown>): void {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;
  try {
    fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(body),
    }).catch(err => logDev(`${table} fetch threw`, err));
  } catch (e) { logDev(`${table} beacon threw`, e); }
}

export function trackPageView(pageId: string): void {
  if (isLikelyBot()) return;
  beacon('linktree_page_views', {
    page_id: pageId,
    session_id: getSessionId(),
    referrer: clamp(typeof document !== 'undefined' ? document.referrer : null, MAX_REFERRER_LEN),
    device: detectDevice(),
    user_agent_hash: uaHash(),
  });
}

export function trackLinkClick(pageId: string, blockId: string | null, url: string | null): void {
  if (isLikelyBot()) return;
  beacon('linktree_link_clicks', {
    page_id: pageId,
    block_id: blockId,
    url: clamp(url, MAX_URL_LEN),
    session_id: getSessionId(),
    referrer: clamp(typeof document !== 'undefined' ? document.referrer : null, MAX_REFERRER_LEN),
    device: detectDevice(),
    user_agent_hash: uaHash(),
  });
}

export function openTrackedLink(pageId: string, blockId: string | null, url: string) {
  trackLinkClick(pageId, blockId, url);
  window.open(url, '_blank', 'noopener,noreferrer');
}
